<script setup lang="ts">
import type { BottleAccessMode, BottleStatus, LocationInput } from '@wilson/shared'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import LocationField from '../../components/LocationField.vue'
import { api, jsonBody } from '../../services/api'

interface PrivateLocation {
  source: string
  latitude?: number
  longitude?: number
  accuracy?: number
  description?: string
}
interface Movement {
  id: string
  type: string
  occurredAt: string
  publicLocation: string | null
  privateLocation: PrivateLocation | null
}
interface BottleDetail {
  bottle: {
    id: string
    name: string
    status: BottleStatus
    accessMode: BottleAccessMode
    quoteId: string
  }
  quote: { id: string; name: string; text: string }
  journey: Movement[]
}
const route = useRoute()
const id = computed(() => String(route.params.id))
const publicOrigin = window.location.origin
const data = ref<BottleDetail | null>(null)
const quotes = ref<Array<{ id: string; name: string }>>([])
const form = ref<{
  name: string
  quoteId: string
  status: BottleStatus
  location?: LocationInput
  physicalQuoteReplaced?: boolean
}>({ name: '', quoteId: '', status: 'draft' })
const editing = ref(false)
const accessBusy = ref(false)
const error = ref('')
async function load() {
  const [detail, quoteData] = await Promise.all([
    api<BottleDetail>(`/admin/bottles/${id.value}`),
    api<{ items: Array<{ id: string; name: string }> }>('/admin/quotes?pageSize=100'),
  ])
  data.value = detail
  quotes.value = quoteData.items
  form.value = {
    name: detail.bottle.name,
    quoteId: detail.bottle.quoteId,
    status: detail.bottle.status,
  }
}
async function save() {
  try {
    await api(`/admin/bottles/${id.value}`, { method: 'PATCH', ...jsonBody(form.value) })
    editing.value = false
    await load()
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Bottle could not be updated.'
  }
}
async function setAccessMode(accessMode: BottleAccessMode) {
  if (!data.value || accessMode === data.value.bottle.accessMode) return
  const messages: Record<BottleAccessMode, string> = {
    active: 'Restore public access and allow eligible finds and finder comments?',
    read_only:
      'Make this bottle read-only? Its public journey will remain visible, but new finds and comments will be blocked.',
    private:
      'Hide this bottle publicly? Visitors will see only a generic unavailable page, while reports and valid re-hide claims remain available.',
  }
  if (!window.confirm(messages[accessMode])) return
  accessBusy.value = true
  error.value = ''
  try {
    await api(`/admin/bottles/${id.value}`, {
      method: 'PATCH',
      ...jsonBody({ accessMode }),
    })
    await load()
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Bottle access could not be updated.'
  } finally {
    accessBusy.value = false
  }
}
function printLabel() {
  window.print()
}
onMounted(load)
</script>

<template>
  <div v-if="data">
    <header class="admin-heading">
      <div>
        <RouterLink class="breadcrumb" to="/admin/bottles">← Bottles</RouterLink>
        <div class="title-with-badges">
          <h1>{{ data.bottle.name }}</h1>
          <span class="status-pill" :class="data.bottle.status">{{ data.bottle.status }}</span>
          <span class="status-pill" :class="data.bottle.accessMode">{{
            data.bottle.accessMode.replace('_', ' ')
          }}</span>
        </div>
        <p class="mono">{{ data.bottle.id }}</p>
      </div>
      <div class="button-row">
        <a
          class="button secondary"
          :href="`/api/v1/admin/bottles/${id}/qr?format=svg`"
          target="_blank"
          >Download QR SVG</a
        ><a
          class="button secondary"
          :href="`/api/v1/admin/bottles/${id}/qr?format=png`"
          target="_blank"
          >Download QR PNG</a
        ><button class="button secondary" type="button" @click="printLabel">Print label</button
        ><button class="button primary" @click="editing = !editing">
          {{ editing ? 'Cancel' : 'Edit bottle' }}
        </button>
      </div>
    </header>
    <p v-if="error" class="form-error banner">{{ error }}</p>
    <form v-if="editing" class="admin-card editor-form" @submit.prevent="save">
      <div class="form-grid">
        <label>Name<input v-model="form.name" required /></label
        ><label
          >Quote<select v-model="form.quoteId">
            <option v-for="quote in quotes" :key="quote.id" :value="quote.id">
              {{ quote.name }}
            </option>
          </select></label
        ><label
          >Status<select v-model="form.status" :disabled="data.bottle.status === 'retired'">
            <option value="draft">Draft</option>
            <option value="hidden">Hidden</option>
            <option value="found" :disabled="data.bottle.status !== 'found'">
              Found (set by finder)
            </option>
            <option value="missing">Missing</option>
            <option value="retired">Retired</option>
          </select></label
        ><label class="check-label"
          ><input v-model="form.physicalQuoteReplaced" type="checkbox" /><span
            >Physical quote replaced if assignment changed</span
          ></label
        ><LocationField v-model="form.location" class="span-2" />
      </div>
      <button class="button primary">Save bottle</button>
    </form>
    <section class="admin-card access-control-card">
      <div>
        <p class="eyebrow">Public availability</p>
        <h2>{{ data.bottle.accessMode.replace('_', ' ') }}</h2>
        <p v-if="data.bottle.accessMode === 'active'">
          The journey is public and eligible visitors can find and comment.
        </p>
        <p v-else-if="data.bottle.accessMode === 'read_only'">
          The public journey remains visible. New finds and comments are blocked; an existing finder
          can still complete re-hide.
        </p>
        <p v-else>
          Public details are hidden. Reports and re-hide by an existing finder remain available.
        </p>
      </div>
      <div class="button-row">
        <button
          v-if="data.bottle.accessMode !== 'read_only'"
          class="button secondary"
          type="button"
          :disabled="accessBusy"
          @click="setAccessMode('read_only')"
        >
          Make read-only
        </button>
        <button
          v-if="data.bottle.accessMode !== 'private'"
          class="button danger"
          type="button"
          :disabled="accessBusy"
          @click="setAccessMode('private')"
        >
          Hide publicly
        </button>
        <button
          v-if="data.bottle.accessMode !== 'active'"
          class="button primary"
          type="button"
          :disabled="accessBusy"
          @click="setAccessMode('active')"
        >
          Restore active
        </button>
      </div>
    </section>
    <div class="admin-grid bottle-detail-grid">
      <section class="admin-card">
        <p class="eyebrow">Current message</p>
        <blockquote class="admin-quote">“{{ data.quote.text }}”</blockquote>
        <RouterLink :to="`/admin/quotes/${data.quote.id}`">{{ data.quote.name }} →</RouterLink>
      </section>
      <section class="admin-card qr-preview print-label">
        <img :src="`/api/v1/admin/bottles/${id}/qr?format=svg`" alt="Bottle QR code" />
        <div>
          <strong>Permanent public link</strong
          ><a :href="`/b/${id}`" target="_blank">{{ publicOrigin }}/b/{{ id }}</a>
        </div>
      </section>
    </div>
    <section class="admin-card">
      <div class="card-heading">
        <div>
          <p class="eyebrow">Immutable history</p>
          <h2>Journey</h2>
        </div>
        <span>{{ data.journey.length }} events</span>
      </div>
      <ol class="admin-timeline">
        <li v-for="movement in data.journey" :key="movement.id">
          <span class="timeline-dot"></span>
          <div>
            <strong>{{ movement.type.replaceAll('_', ' ') }}</strong>
            <p>
              {{ new Date(movement.occurredAt).toLocaleString()
              }}<template v-if="movement.publicLocation">
                · Public: {{ movement.publicLocation }}</template
              >
            </p>
            <code v-if="movement.privateLocation">Private: {{ movement.privateLocation }}</code>
          </div>
        </li>
      </ol>
    </section>
  </div>
</template>
